import React, {useMemo} from "react";
import {statisticalType} from "@icoach/accountBook/staticData";
import useOrderStatisticsApi from "@apis/useOrderStatisticsApi";
import {
    appendSummaryRow,
    getAccountBookOrderStatOrderTypeForAllRowLabel,
    getAccountBookOrderStatPaymentTypeForAllRowLabel
} from "@icoach/accountBook/components/AccountBookOrderStatTemplate";
import { ACCOUNT_BOOK_ALL_ORDER_STAT_URL } from "@icoach/router/routerPath";
import { parseDate } from "@util/moment";
import { Link } from "react-router-dom";
import { isEmpty, mergeQueryToUrl } from "@util/utils";
import { TabsCardLayout } from "@icoach/components";
import { Button, DateIntervalField, Stack } from "@curves/ui-base";
import OverviewListLayout from "@icoach/components/overviewList/OverviewListLayout";
import useDateRangeFilter from "@util/hook/useDateRangeFilter";
import useExcelExportFlow from "@util/hook/useExcelExportFlow";
import useOrderStatisticsExportApi from "@apis/useOrderStatisticsExportApi";
import useOverview from "@util/hook/useOverview";

const overviewDataMap = {
    [statisticalType.orderType]: {
        headerRow: getAccountBookOrderStatOrderTypeForAllRowLabel(),
        sumKeys: [
            "newSalePrice",
            "membershipPrice",
            "productSalePrice",
            "healthcareSalePrice",
            "cabinetPrice",
            "deviceOrderSalePrice",
            "allOffsitePrice",
            "bonusPrice",
            "deviceOrderBonusPrice",
            "totalPrice"
        ]
    },
    [statisticalType.paymentType]: {
        headerRow: getAccountBookOrderStatPaymentTypeForAllRowLabel(),
        sumKeys: ["cashTotalPrice", "allCardTotalPrice", "achPrice", "inwardRemitPrice", "outwardRemitPrice", "totalPrice"]
    }
};

const AccountBookOrderStatOnAllPage = ({dialogRef}) => {
    // 取得日期範圍欄位與相關驗證
    const {
        dateRangeRef,
        getDateParams,
        validateStartDate,
        validateEndDate,
    } = useDateRangeFilter({maxRange: 3, rangeUnit: 'months'});

    // 取得查詢參數
    const getParams = () => {
        const { startDate: start, endDate: end } = getDateParams();
        return {
            tabIndex: searchParams.tabIndex,
            start,
            end
        };
    }

    // 取得概覽資料
    const {getOrderStatisticsAllApi} = useOrderStatisticsApi();
    const {searchParams, overviewData, getOverviewData} = useOverview({
        getOverviewApi: getOrderStatisticsAllApi,
        defaultSearchParams: {
            tabIndex: statisticalType.orderType,
            start: parseDate(),
            end: parseDate()
        },
        isFetchOnMount: true
    });

    // 處理 Excel 匯出
    const {postOrderStatisticsExportAllApi} = useOrderStatisticsExportApi();
    const { onExcelOnClick } = useExcelExportFlow({
        validate: () => !dateRangeRef.current.isError(),
        getExportParams: getParams,
        exportApi: postOrderStatisticsExportAllApi
    });

    //  處理提交事件
    const handleSubmit = () => {
        if (!dateRangeRef.current.isError()) {
            getOverviewData({
                ...getParams(),
                tabIndex: searchParams.tabIndex
            });
        }
    };

    const tabsData = useMemo(
        () => {
            const baseQuery = {
                ...searchParams,
                tabIndex: undefined,
                start: searchParams?.start ?? parseDate(),
                end: searchParams?.end ?? parseDate(),
            };
            return [
                {
                    label: "收入來源",
                    value: statisticalType.orderType,
                    component: Link,
                    to: mergeQueryToUrl(ACCOUNT_BOOK_ALL_ORDER_STAT_URL, {
                        ...baseQuery,
                        tabIndex: statisticalType.orderType
                    })
                },
                {
                    label: "收款方式",
                    value: statisticalType.paymentType,
                    component: Link,
                    to: mergeQueryToUrl(ACCOUNT_BOOK_ALL_ORDER_STAT_URL, {
                        ...baseQuery,
                        tabIndex: statisticalType.paymentType
                    })
                }
            ];
        },
        // eslint-disable-next-line
        [searchParams]
    );

    // 取得當前概覽資料
    const currentOverviewData = overviewDataMap[searchParams.tabIndex] || {};

    const list = useMemo(() => {
            if (isEmpty(currentOverviewData)) return [];
            return [
                ...overviewData?.list,
                appendSummaryRow(overviewData?.list, currentOverviewData?.headerRow, currentOverviewData?.sumKeys)
            ]
        },
        // eslint-disable-next-line
        [JSON.stringify(overviewData.list), JSON.stringify(currentOverviewData)]);

    return (
        <TabsCardLayout tabs={tabsData} activeTab={searchParams?.tabIndex}>
            <OverviewListLayout className={"p-3"}>
                <OverviewListLayout.SearchBar className={"bg-light mb-2"} onSubmit={handleSubmit}>
                    <Stack justifyContent={"start"} alignItems={"start"} spacing={2}>
                        <DateIntervalField
                            key={`${searchParams?.start}_${searchParams?.end}`}
                            ref={dateRangeRef}
                            label={"建立日期"}
                            className={"flex-auto"}
                            direction={"row"}
                            startDateProps={{
                                defaultValue: searchParams?.start,
                                onChange: validateStartDate
                            }}
                            endDateProps={{
                                defaultValue: searchParams?.end,
                                onChange: validateEndDate
                            }}
                            fullWidth
                        />
                        <Button type={"submit"} className={"white-space-nowrap"} variant={"contained"}>
                            搜尋
                        </Button>
                        <Button className={"white-space-nowrap"} variant={"contained"} color="success"
                                onClick={onExcelOnClick}>
                            下載Excel
                        </Button>
                    </Stack>
                </OverviewListLayout.SearchBar>
                <OverviewListLayout.Table
                    headerRow={currentOverviewData?.headerRow || []}
                    list={list}
                    tableRowBodyProps={{dialogRef}}
                    isGray
                />
            </OverviewListLayout>
        </TabsCardLayout>
    )
};

export default AccountBookOrderStatOnAllPage;
